<template>
  <div class="coffee-card">
    <div class="capsule-top">
      <div class="image-container">
        <img v-if="coffee.imgUrl" :src="coffee.imgUrl" :alt="coffee.name" class="coffee-image" />
        <div v-else class="no-image-placeholder"></div>
      </div>
      <div class="indicators">
        <div class="capsule-attribute">
          <div class="circle-background">
            <svg width="36" height="36" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" stroke="#E5D5BB" stroke-width="2"/>
              <path
                :d="getArcPath()"
                fill="none"
                stroke="#8B4513"
                stroke-width="2"
                stroke-linecap="round"
              />
              <text x="18" y="22" text-anchor="middle" font-size="12" fill="#8B4513" font-weight="bold">{{ coffee.intensity }}</text>
            </svg>
          </div>
          <div class="capsule-attribute-label">Интенсивность</div>
        </div>

        <div class="capsule-attribute" v-if="sizeIcon">
          <div class="circle-background">
            <SvgIcon :name="sizeIcon" width="28px" height="28px" fill="#876C43" />
          </div>
          <div class="capsule-attribute-label">{{ sizeLabel }}</div>
        </div>
      </div>
    </div>
    <div class="capsule-bottom">
      <div class="product-name">{{ coffee.name }}</div>
      <div class="product-description">{{ coffee.description }}</div>
      <div class="price-section">
        <div class="price-text">₽{{ coffee.price }}/10</div>
        <div class="button-container">
          <button v-if="quantity === 0" class="add-button" @click="initialAddToCart">+</button>
          <div v-else class="quantity-controls">
            <button class="quantity-btn" @click="decrement">-</button>
            <span class="quantity-text">{{ quantity }}</span>
            <button class="quantity-btn" @click="increment">+</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import SvgIcon from './SvgIcon.vue';

export default {
  components: {
    SvgIcon
  },
  props: {
    coffee: Object,
    quantity: Number,
    category: String,
  },
  emits: ['update-quantity'],
  setup(props, { emit }) {
    const increment = () => emit('update-quantity', { coffee: props.coffee, change: 1 });
    const decrement = () => emit('update-quantity', { coffee: props.coffee, change: -1 });
    const initialAddToCart = () => emit('update-quantity', { coffee: props.coffee, change: 1 });

    const sizeMappings = {
      '40 мл': { icon: 'sizes/espresso', label: 'Espresso' },
      '80 мл': { icon: 'sizes/doubleespresso', label: 'Double Espresso' },
      '110 мл': { icon: 'sizes/lungo', label: 'Lungo' },
      '150 мл': { icon: 'sizes/granlungo', label: 'Gran Lungo' },
      '230 мл': { icon: 'sizes/signature', label: 'Signature' }
    };

    const sizeIcon = computed(() => sizeMappings[props.category]?.icon || '');
    const sizeLabel = computed(() => sizeMappings[props.category]?.label || props.category);

    const getArcPath = () => {
      const intensity = props.coffee.intensity || 0;
      const segments = 12;
      const filledSegments = Math.min(intensity, segments);

      if (filledSegments === 0) return '';

      const angle = (filledSegments / segments) * 360;
      const startAngle = -90;
      const endAngle = startAngle + angle;

      const radius = 16;
      const centerX = 18;
      const centerY = 18;

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);

      const largeArc = angle > 180 ? 1 : 0;

      return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
    };

    return { increment, decrement, initialAddToCart, getArcPath, sizeIcon, sizeLabel };
  },
};
</script>

<style scoped>
.coffee-card {
  display: flex;
  flex-direction: column;
  max-width: 200px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  background: #FAF9F8;
}

.coffee-card:hover {
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px);
}

.capsule-top {
  background: #F0EDE5;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.image-container {
  width: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coffee-image {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.no-image-placeholder {
  width: 100%;
  height: 120px;
  background: #E5D5BB;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.indicators {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.capsule-attribute {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.circle-background {
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
}

.capsule-attribute-label {
  font-size: 10px;
  font-weight: bold;
  color: #876C43;
  text-align: center;
  margin-top: 4px;
  line-height: 1.1;
}

.capsule-bottom {
  background: #FAF9F8;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
  line-height: 1.2;
  min-height: 32px;
}

.product-description {
  font-size: 11px;
  color: #666;
  text-align: center;
  line-height: 1.3;
  min-height: 40px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.price-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  gap: 8px;
}

.price-text {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.button-container {
  display: flex;
  align-items: center;
}

.add-button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgb(37, 122, 87);
  color: white;
  border: none;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  padding: 0;
}

.add-button:hover {
  background-color: rgb(30, 100, 70);
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quantity-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgb(37, 122, 87);
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  padding: 0;
}

.quantity-btn:hover {
  background-color: rgb(30, 100, 70);
}

.quantity-text {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  min-width: 20px;
  text-align: center;
}

@media (max-width: 768px) {
  .coffee-card {
    max-width: 100%;
  }
}
</style>